/**
 * SaveSystem — localStorage-based persistence for game progress.
 *
 * Ported directly from the Phaser codebase. This system is engine-agnostic
 * (uses only browser localStorage), so the API is identical.
 *
 * Tracks which levels are complete, which co-op abilities are unlocked,
 * and checkpoint data within levels.
 */

const SAVE_KEY = 'cats-of-chronos-save';
const VOLUMES_KEY = 'cats-of-chronos-volumes';
const CHECKPOINT_KEY = 'cats-of-chronos-checkpoint';

export interface VolumePrefs {
  master: number;
  sfx: number;
  music: number;
}

export const DEFAULT_VOLUMES: VolumePrefs = {
  master: 1.0,
  sfx: 0.5,
  music: 0.4,
};

export interface SaveData {
  currentLevel: string;
  levelsCompleted: string[];
  coopAbilitiesUnlocked: string[];
  /**
   * Per-Elder Making Biscuits state. True once the player has seen the
   * full vision for that Elder; subsequent biscuit sessions play a
   * shorter replay "echo" (see SPEC-making-biscuits.md §2). Missing keys
   * default to false so pre-existing saves auto-migrate.
   */
  biscuitsSeen: Partial<Record<BiscuitElder, boolean>>;
}

export type BiscuitElder = "leo" | "gus" | "kitty";

export const CHECKPOINT_VERSION = 2;

export interface CheckpointData {
  version: number;            // CHECKPOINT_VERSION = 2
  levelId: string;
  puzzleIndex: number;        // index of last-solved puzzle = restore point
  solvedPuzzleIds: string[];
  cats: {
    minna: { x: number; y: number; z: number };
    basil: { x: number; y: number; z: number };
  };
  harnessIntegrity: number;   // Basil harness 0..max
  energy: { minna: number; basil: number };
  puzzleState: Record<string, number | boolean | string>; // SCALARS ONLY
}

const DEFAULT_SAVE: SaveData = {
  currentLevel: 'LiminalAtrium',
  levelsCompleted: [],
  coopAbilitiesUnlocked: [],
  biscuitsSeen: {},
};

export class SaveSystem {
  private static instance: SaveSystem | null = null;

  static getInstance(): SaveSystem {
    if (!SaveSystem.instance) {
      SaveSystem.instance = new SaveSystem();
    }
    return SaveSystem.instance;
  }

  private data: SaveData;

  constructor() {
    this.data = this.load();
  }

  private load(): SaveData {
    try {
      const raw = localStorage.getItem(SAVE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          currentLevel: parsed.currentLevel ?? DEFAULT_SAVE.currentLevel,
          levelsCompleted: Array.isArray(parsed.levelsCompleted) ? [...parsed.levelsCompleted] : [],
          coopAbilitiesUnlocked: Array.isArray(parsed.coopAbilitiesUnlocked) ? [...parsed.coopAbilitiesUnlocked] : [],
          biscuitsSeen: parsed.biscuitsSeen && typeof parsed.biscuitsSeen === "object"
            ? { ...parsed.biscuitsSeen }
            : {},
        };
      }
    } catch {
      // Corrupted save — reset
    }
    return {
      currentLevel: DEFAULT_SAVE.currentLevel,
      levelsCompleted: [],
      coopAbilitiesUnlocked: [],
      biscuitsSeen: {},
    };
  }

  private persist(): void {
    try {
      localStorage.setItem(SAVE_KEY, JSON.stringify(this.data));
    } catch {
      // localStorage not available (e.g., private browsing quota)
    }
  }

  /** Mark a level as completed. */
  completeLevel(levelKey: string): void {
    if (!this.data.levelsCompleted.includes(levelKey)) {
      this.data.levelsCompleted.push(levelKey);
    }
    this.persist();
  }

  /** Check if a level is completed. */
  isLevelComplete(levelKey: string): boolean {
    return this.data.levelsCompleted.includes(levelKey);
  }

  /** Unlock a co-op ability. */
  unlockCoopAbility(abilityId: string): void {
    if (!this.data.coopAbilitiesUnlocked.includes(abilityId)) {
      this.data.coopAbilitiesUnlocked.push(abilityId);
    }
    this.persist();
  }

  /** Check if a co-op ability is unlocked. */
  isCoopAbilityUnlocked(abilityId: string): boolean {
    return this.data.coopAbilitiesUnlocked.includes(abilityId);
  }

  /** Set current level. */
  setCurrentLevel(levelKey: string): void {
    this.data.currentLevel = levelKey;
    this.persist();
  }

  /** Get current level. */
  getCurrentLevel(): string {
    return this.data.currentLevel;
  }

  /**
   * Persist the single active checkpoint into its own localStorage slot,
   * isolated from progress (SAVE_KEY) exactly like volumes/bindings. At most
   * one checkpoint is stored at a time; the level it belongs to is carried in
   * the payload's `levelId`.
   */
  saveCheckpoint(d: CheckpointData): void {
    try {
      localStorage.setItem(CHECKPOINT_KEY, JSON.stringify(d));
    } catch {
      // localStorage unavailable (private browsing / quota) — drop silently.
    }
  }

  /**
   * Load the active checkpoint for `levelId`. Returns null when the slot is
   * absent, corrupted, version-mismatched (older schema), or belongs to a
   * different level. The version gate means schema bumps auto-invalidate
   * stale checkpoints instead of restoring a malformed world.
   */
  getCheckpoint(levelId: string): CheckpointData | null {
    try {
      const raw = localStorage.getItem(CHECKPOINT_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as Partial<CheckpointData>;
      if (parsed.version !== CHECKPOINT_VERSION) return null;
      if (parsed.levelId !== levelId) return null;
      return parsed as CheckpointData;
    } catch {
      return null;
    }
  }

  /**
   * Remove the active checkpoint, but only if it belongs to `levelId`, so a
   * level never wipes another level's checkpoint.
   */
  clearCheckpoint(levelId: string): void {
    const current = this.getCheckpoint(levelId);
    if (current === null) return;
    try {
      localStorage.removeItem(CHECKPOINT_KEY);
    } catch {
      // localStorage unavailable — nothing to clear.
    }
  }

  /** Get all save data. */
  getData(): Readonly<SaveData> {
    return this.data;
  }

  /** Reset all progress. */
  reset(): void {
    this.data = {
      currentLevel: DEFAULT_SAVE.currentLevel,
      levelsCompleted: [],
      coopAbilitiesUnlocked: [],
      biscuitsSeen: {},
    };
    this.persist();
  }

  // ─── Making Biscuits ────────────────────────────────────────────

  /**
   * Mark an Elder's biscuit vision as seen. Persists immediately so a
   * crash between "vision played" and "level completed" still records
   * the one-shot flag.
   */
  markBiscuitSeen(elder: BiscuitElder): void {
    this.data.biscuitsSeen = { ...this.data.biscuitsSeen, [elder]: true };
    this.persist();
  }

  /**
   * Whether the player has already experienced this Elder's full vision.
   * When true, subsequent biscuit sessions trigger the "replay echo"
   * variant — same lines, shorter dwell, smaller beat count.
   */
  hasSeenBiscuit(elder: BiscuitElder): boolean {
    return this.data.biscuitsSeen?.[elder] === true;
  }

  /** Save custom control bindings. */
  saveBindings(bindings: any): void {
    try {
      localStorage.setItem('cats-of-chronos-bindings', JSON.stringify(bindings));
    } catch {}
  }

  /** Load custom control bindings. */
  loadBindings(): any {
    try {
      const raw = localStorage.getItem('cats-of-chronos-bindings');
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  /**
   * Persist volume preferences in a separate localStorage slot.
   * Intentionally isolated from the progress save key so "reset all progress"
   * never wipes the user's volume settings.
   */
  saveVolumes(v: VolumePrefs): void {
    try {
      localStorage.setItem(VOLUMES_KEY, JSON.stringify(v));
    } catch {}
  }

  /**
   * Load persisted volume preferences.
   * Returns DEFAULT_VOLUMES if the slot is absent, corrupted, or localStorage
   * throws. Each channel is clamped to [0, 1] on load so out-of-range values
   * written by older builds can never propagate into the audio pipeline.
   */
  loadVolumes(): VolumePrefs {
    try {
      const raw = localStorage.getItem(VOLUMES_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<VolumePrefs>;
        const clamp = (v: unknown, def: number): number => {
          const n = typeof v === 'number' ? v : def;
          return Math.max(0, Math.min(1, n));
        };
        return {
          master: clamp(parsed.master, DEFAULT_VOLUMES.master),
          sfx:    clamp(parsed.sfx,    DEFAULT_VOLUMES.sfx),
          music:  clamp(parsed.music,  DEFAULT_VOLUMES.music),
        };
      }
    } catch {
      // localStorage unavailable or JSON corrupted — return defaults silently.
    }
    return { ...DEFAULT_VOLUMES };
  }
}
